import * as React from 'react';
import { classNamesFunction, FontIcon } from '@fluentui/react';
import {
    IGroupedComboBoxGroupHeaderStyles,
    IGroupedComboBoxGroupHeaderStyleProps,
    IGroupedComboBoxGroupHeaderProps,
} from './GroupedComboBoxGroupHeader.types';
import { DropDownContext } from 'grouped-combobox-core';
import { IGroupedComboBoxGroup, IGroupedComboBoxItem } from 'grouped-combobox-core';
import { assert } from 'textfield-with-action';
import preventDefaultCallback from '../utils/preventDefaultCallback';

const getClassNames = classNamesFunction<
    IGroupedComboBoxGroupHeaderStyleProps,
    IGroupedComboBoxGroupHeaderStyles
>();

function fakeItemFromGroup(group: IGroupedComboBoxGroup): IGroupedComboBoxItem {
    return { text: group.text, itemKey: group.groupKey };
}

export const GroupHeaderBase: React.FunctionComponent<IGroupedComboBoxGroupHeaderProps> = (
    props: IGroupedComboBoxGroupHeaderProps
) => {
    const { className, group, isCollapsed, onToggleCollapse, styles, theme } = props;

    assert(theme);

    const classNames = getClassNames(styles, {
        theme: theme,
        className,
        isCollapsed,
    });

    const { onItemMouseEnter, iconResources } = React.useContext(DropDownContext);

    const onMouseOverOrFocus = React.useCallback(
        (ev) => {
            // This causes the open submenu to close when hovering over the group's toggle button
            onItemMouseEnter?.(fakeItemFromGroup(group), ev);
        },
        [group, onItemMouseEnter]
    );

    const onMouseUpCallback = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            onToggleCollapse?.();

            ev.stopPropagation();
            ev.preventDefault();
        },
        [onToggleCollapse]
    );

    const onKeyDownCallback = React.useCallback(
        (ev: React.KeyboardEvent<HTMLDivElement>) => {
            if (ev.key === 'Enter' || ev.key == ' ') {
                onToggleCollapse?.();

                ev.stopPropagation();
                ev.preventDefault();
            }
        },
        [onToggleCollapse]
    );

    const collapseButton =
        onToggleCollapse !== undefined ? (
            <div
                role="button"
                aria-label={group.text}
                aria-expanded={!isCollapsed}
                data-is-focusable={true}
                className={classNames.expand}
                onMouseDown={preventDefaultCallback}
                onMouseUp={onMouseUpCallback}
                onKeyDown={onKeyDownCallback}
                onMouseOver={onMouseOverOrFocus}
                onFocus={onMouseOverOrFocus}
                tabIndex={-1}
            >
                <FontIcon
                    className={classNames.expandIsCollapsed}
                    iconName={iconResources?.chevronDownName ?? 'ChevronDown-svg'}
                />
            </div>
        ) : undefined;

    return (
        <div className={classNames.root}>
            {group.icon && <FontIcon className={classNames.icon} iconName={group.icon?.source} />}
            {group.onRenderHeaderLabel ? (
                group.onRenderHeaderLabel({
                    group,
                    isCollapsed,
                    labelStyle: classNames.title,
                })
            ) : (
                <h1 className={classNames.title}>{group.text}</h1>
            )}

            {collapseButton}
        </div>
    );
};
